import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  BarChart3, 
  Navigation, 
  Clock, 
  StickyNote, 
  Bookmark, 
  Video, 
  Brain, 
  GraduationCap, 
  FileText, 
  Activity,
  TrendingUp, 
  Flame, 
  Award, 
  Lightbulb 
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dashboardImage from "@/assets/dashboard-showcase.png";

const features = [
  { icon: BarChart3, key: "progress", category: "dashboard" },
  { icon: Navigation, key: "navigation", category: "dashboard" },
  { icon: Clock, key: "timeTracking", category: "dashboard" },
  { icon: StickyNote, key: "notes", category: "learning" },
  { icon: Bookmark, key: "bookmarks", category: "learning" },
  { icon: Video, key: "videoFeatures", category: "learning" },
  { icon: Brain, key: "flashcards", category: "study" },
  { icon: GraduationCap, key: "quizzes", category: "study" },
  { icon: FileText, key: "resources", category: "study" },
  { icon: Activity, key: "studyAnalytics", category: "study" },
  { icon: TrendingUp, key: "detailedAnalytics", category: "progress" },
  { icon: Flame, key: "streaks", category: "progress" },
  { icon: Award, key: "certificates", category: "progress" },
  { icon: Lightbulb, key: "insights", category: "progress" },
];

const categoryGradients = {
  dashboard: "from-primary/20 to-accent/20",
  learning: "from-accent/20 to-secondary/20",
  study: "from-secondary/20 to-primary/20",
  progress: "from-primary/20 to-accent/20",
};

export const LearningDashboardShowcase = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="py-12 lg:py-16 bg-gradient-subtle relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10" />
      <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[120px] -z-10" />
      
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 space-y-3 max-w-4xl mx-auto"
        >
          <Badge variant="secondary" className="mb-3 px-3 py-1">
            {t("dashboardShowcase.badge")}
          </Badge>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 bg-gradient-primary bg-clip-text text-transparent">
            {t("dashboardShowcase.title")}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-3xl mx-auto">
            {t("dashboardShowcase.subtitle")}
          </p>
        </motion.div>

        {/* Dashboard Screenshot - Full Width on Top */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-10 relative max-w-5xl mx-auto"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-glass border border-white/10 bg-card/30 backdrop-blur-xl hover:shadow-glass-hover transition-shadow duration-500">
            <img
              src={dashboardImage}
              alt={t("dashboardShowcase.imageAlt")}
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none" />
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="space-y-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="text-center space-y-2"
          >
            <h3 className="text-xl md:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Complete Learning Experience
            </h3>
            <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
              Everything you need from enrollment to certification
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const gradientClass = categoryGradients[feature.category as keyof typeof categoryGradients];
              
              return (
                <motion.div
                  key={feature.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.8 + index * 0.05 }}
                >
                  <Card className="h-full min-h-[200px] bg-card/30 backdrop-blur-xl border-white/10 hover:border-primary/30 transition-all duration-300 group hover:shadow-glass-hover hover:scale-[1.02]">
                    <CardContent className="p-4 space-y-3">
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradientClass} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold min-h-[40px] text-foreground group-hover:text-primary transition-colors">
                          {t(`dashboardShowcase.features.${feature.key}.title`)}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {t(`dashboardShowcase.features.${feature.key}.description`)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 1.2 }}
          className="text-center"
        >
          <Button
            size="default"
            onClick={() => navigate("/dashboard")}
            className="px-6 py-3 text-sm shadow-glass hover:shadow-glass-hover hover:scale-105 transition-all duration-300"
          >
            {t("dashboardShowcase.cta")}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
